import React, { useState, useEffect, useRef } from 'react';
import { useSession } from '../Utils/SessionContext';
import usePopupClose from '../Utils/usePopupClose';

const AddJobPopup = ({ isOpen, onClose, onSubmit }) => {
  const { user } = useSession();
  
  const [formData, setFormData] = useState({
    companyLink: '',
    extraInfo: '',
    apikey: '',
    internalID: '',
    generateVsJobEvaluation: true
  });
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = usePopupClose(isOpen, onClose);

  // Set API key from session when component mounts or when user changes
  useEffect(() => {
    if (user && user.apikey) {
      setFormData(prevData => ({
        ...prevData,
        apikey: user.apikey
      }));
    }
  }, [user]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        companyLink: '',
        extraInfo: '',
        apikey: '',
        internalID: '',
        generateVsJobEvaluation: true
      });
      setJobDescriptionFile(null);
      setErrors({});
      setSubmitError(null);
      setIsSubmitting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        // Check file size (5MB limit)
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB <= 5) {
          setJobDescriptionFile(file);
          setErrors({ ...errors, jobDescriptionFile: null });
        } else {
          setJobDescriptionFile(null);
          setErrors({ ...errors, jobDescriptionFile: 'File size exceeds 5MB limit' });
        }
      } else {
        setJobDescriptionFile(null);
        setErrors({ ...errors, jobDescriptionFile: 'Please upload a PDF file' });
      }
    }
  };

  const handleCompanyLinkChange = (e) => {
    let value = e.target.value;
    
    // If the user is starting to type and hasn't included a protocol
    if (value && !value.match(/^https?:\/\//)) {
      // If they're explicitly typing http://, don't modify
      if (value.match(/^http:\/?\/?$/)) {
        // Do nothing, they're typing http://
      }
      // If they're explicitly typing https://, don't modify
      else if (value.match(/^https:\/?\/?$/)) {
        // Do nothing, they're typing https://
      }
      // Otherwise, automatically add https:// prefix
      else {
        value = `https://${value}`;
      }
    }
    
    setFormData({
      ...formData,
      companyLink: value
    });
    
    if (errors.companyLink) {
      setErrors({
        ...errors,
        companyLink: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate job description file (mandatory)
    if (!jobDescriptionFile) {
      newErrors.jobDescriptionFile = 'Job description PDF is required';
    }
    
    // Validate company link format if provided
    if (formData.companyLink && !formData.companyLink.match(/^https?:\/\/.+/)) {
      newErrors.companyLink = 'Company link must start with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        const formDataToSubmit = {
          jobDescriptionFile,
          companyLink: formData.companyLink.trim() || null,
          extraInfo: formData.extraInfo.trim() || null,
          apikey: formData.apikey.trim() || null,
          internalID: formData.internalID.trim() || null,
          generateVsJobEvaluation: formData.generateVsJobEvaluation
        };
        
        await onSubmit(formDataToSubmit);
        onClose(); // Close popup on success
      } catch (error) {
        console.error('Error creating job:', error);
        setSubmitError(error.message || 'Failed to create job. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Add New Job</h2>
          <p className="text-gray-600 mt-1">Upload a job description PDF to create a new job</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Job Description PDF Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description PDF <span className="text-red-500">*</span>
            </label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
              errors.jobDescriptionFile ? 'border-red-300' : 'border-gray-300'
            }`}>
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept=".pdf"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF up to 5MB</p>
                {jobDescriptionFile && (
                  <p className="text-sm text-green-600">
                    Selected: {jobDescriptionFile.name}
                  </p>
                )}
              </div>
            </div>
            {errors.jobDescriptionFile && (
              <p className="mt-2 text-sm text-red-600">{errors.jobDescriptionFile}</p>
            )}
          </div>
          
          {/* Company Link */}
          <div>
            <label htmlFor="companyLink" className="block text-sm font-medium text-gray-700 mb-1">
              Company Website Link (Optional)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                id="companyLink"
                name="companyLink"
                value={formData.companyLink}
                onChange={handleCompanyLinkChange}
                className={`block w-full pl-10 rounded-md sm:text-sm p-2 border ${
                  errors.companyLink ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="https://example.com"
              />
            </div>
            {errors.companyLink && (
              <p className="mt-1 text-sm text-red-600">{errors.companyLink}</p>
            )}
          </div>
          
          {/* Extra Information */}
          <div>
            <label htmlFor="extraInfo" className="block text-sm font-medium text-gray-700 mb-1">
              Extra Information (Optional)
            </label>
            <div className="mt-1">
              <textarea
                id="extraInfo"
                name="extraInfo"
                rows={4}
                className="block w-full focus:outline-none sm:text-sm rounded-md p-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add any additional information about the job"
                value={formData.extraInfo}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Generation Options */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h3 className="text-md font-medium text-gray-700 mb-3">Generation Options</h3>
            
            <div className="space-y-2">
              {/* Generate VsJob Evaluation */}
              <div className="flex items-center">
                <input
                  id="generateVsJobEvaluation"
                  name="generateVsJobEvaluation"
                  type="checkbox"
                  checked={formData.generateVsJobEvaluation}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="generateVsJobEvaluation" className="ml-2 block text-sm font-medium text-gray-700">
                  Generate First Evaluation Template (VSJob)
                </label>
              </div>
            </div>
          </div>
          
          {/* Testing Fields Group */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100" hidden>
            <h3 className="text-md font-medium text-gray-700 mb-3">For API testing</h3>
            
            {/* API Key */}
            <div className="mb-3">
              <label htmlFor="apikey" className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <input
                type="text"
                id="apikey"
                name="apikey"
                value={formData.apikey}
                onChange={handleChange}
                className={`block w-full rounded-md sm:text-sm p-2 border ${
                  errors.apikey ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Enter API key"
              />
              {errors.apikey && (
                <p className="mt-1 text-sm text-red-600">{errors.apikey}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 italic">
                If API key is provided, it will be used for authentication. If left empty, your session token will be used instead.
              </p>
            </div>
            
            
            {/* Internal ID */}
            <div>
              <label htmlFor="internalID" className="block text-sm font-medium text-gray-700 mb-1">
                Internal ID
              </label>
              <input
                type="text"
                id="internalID"
                name="internalID"
                value={formData.internalID}
                onChange={handleChange}
                className="block w-full rounded-md sm:text-sm p-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter internal ID"
              />
            </div>
            
          </div>
          
          {/* Warning Message */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-base text-l text-yellow-700">
                  Processing a new job takes ~20 minutes.
                </p>
              </div>
            </div>
          </div>
          
          {/* Error Message */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create Job'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddJobPopup;
